"use client"

import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import { FormEvent, useState } from "react"
import { Copy, ExternalLink, KeyRound, Loader2, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { usePermissions } from "@/hooks/use-permissions"
import { useApiKeysQuery } from "@/hooks/queries/api-keys"
import { useCreateApiKeyMutation, useDeleteApiKeyMutation } from "@/hooks/mutations/api-keys"
import { API_KEYS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useTranslations } from "next-intl"


function formatDate(value: string, dateLocale: string = "en-US") {
  return new Intl.DateTimeFormat(dateLocale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
}

export function ApiKeysPage() {
  const {locale: uiDateLocale} = useUiControlLabels()

  const tUi = useTranslations("ApiKeys.ApiKeysApiKeysPage")
  const tCommonUi = useTranslations("Common")

  const { can } = usePermissions()
  const apiKeys = useApiKeysQuery()
  const createMutation = useCreateApiKeyMutation()
  const deleteMutation = useDeleteApiKeyMutation()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [name, setName] = useState("")
  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  if (!can(API_KEYS_PERMISSIONS.MANAGE)) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <AlertTitle><span key={tUi("accessRestricted")}>{tUi("accessRestricted")}</span></AlertTitle>
            <AlertDescription><span key={tUi("youDonTHavePermissionToManageAPI")}>{tUi("youDonTHavePermissionToManageAPI")}</span></AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  const createApiKey = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const created = await createMutation.mutateAsync({ name })
      setName("")
      setIsCreateOpen(false)
      setCreatedToken(created.token)
    } catch {
      // withQueryHandling already renders the error toast.
    }
  }

  const revokeApiKey = async () => {
    if (!deletingId) return
    try {
      await deleteMutation.mutateAsync({ id: deletingId })
      setDeletingId(null)
    } catch {
      // withQueryHandling already renders the error toast.
    }
  }

  const copyToken = async () => {
    if (!createdToken) return
    try {
      await navigator.clipboard.writeText(createdToken)
      toast.success(tUi("aPIKeyCopied"))
    } catch {
      toast.error(tUi("unableToCopyAPIKey"))
    }
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold"><KeyRound className="h-5 w-5" /> <span><span key={tUi("aPIKeys")}>{tUi("aPIKeys")}</span></span></h1>
            <p className="mt-1 text-sm text-muted-foreground"><span key={tUi("createCredentialsForTenantScopedAPIAccessEach")}>{tUi("createCredentialsForTenantScopedAPIAccessEach")}</span></p>
            <p className="mt-2 text-sm text-muted-foreground">
              <span><span key={tUi("tenantAPIDocumentationIsAvailableAt")}>{tUi("tenantAPIDocumentationIsAvailableAt")}</span></span>{" "}
              <a
                href="/api/tenant-api/docs"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
              >
                <span><span key={tUi("tenantAPIDocs")}>{tUi("tenantAPIDocs")}</span></span>
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <span>.</span>
            </p>
          </div>
          <Button onClick={() => setIsCreateOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> <span><span key={tUi("newAPIKey")}>{tUi("newAPIKey")}</span></span></Button>
        </div>

        {apiKeys.isError && (
          <Alert variant="destructive"><AlertDescription><span key={tUi("unableToLoadAPIKeysPleaseRefreshAnd")}>{tUi("unableToLoadAPIKeysPleaseRefreshAnd")}</span></AlertDescription></Alert>
        )}

        {apiKeys.isLoading ? (
          <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> <span><span key={tUi("loadingAPIKeys")}>{tUi("loadingAPIKeys")}</span></span>
          </div>
        ) : apiKeys.data?.length ? (
          <div className="space-y-2">
            {apiKeys.data.map(apiKey => (
              <Card key={apiKey.id}>
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{apiKey.name}</p>
                    <p className="mt-1 text-sm text-muted-foreground"><span><span key={tUi("created")}>{tUi("created")}</span></span> {formatDate(apiKey.created_at, uiDateLocale)}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setDeletingId(apiKey.id)} disabled={deleteMutation.isPending}>
                    <Trash2 className="mr-1.5 h-4 w-4" /> <span><span key={tUi("revoke")}>{tUi("revoke")}</span></span>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardHeader><CardTitle><span key={tUi("noAPIKeys")}>{tUi("noAPIKeys")}</span></CardTitle><CardDescription><span key={tUi("createAnAPIKeyToConnectTenantScoped")}>{tUi("createAnAPIKeyToConnectTenantScoped")}</span></CardDescription></CardHeader>
          </Card>
        )}
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <form onSubmit={createApiKey} autoComplete="off">
            <DialogHeader><DialogTitle><span key={tUi("createAPIKey")}>{tUi("createAPIKey")}</span></DialogTitle><DialogDescription><span key={tUi("chooseAClearNameSoYourTeamCan")}>{tUi("chooseAClearNameSoYourTeamCan")}</span></DialogDescription></DialogHeader>
            <div className="py-5"><Label htmlFor="api-key-name"><span key={tUi("name")}>{tUi("name")}</span></Label><Input id="api-key-name" name="api-key-name" className="mt-2" value={name} onChange={event => setName(event.target.value)} maxLength={80} autoComplete="off" autoCorrect="off" spellCheck={false} autoFocus required /></div>
            <DialogFooter>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={createMutation.isPending ? tUi("creatingKey") : tUi("createKey")}>{createMutation.isPending ? tUi("creatingKey") : tUi("createKey")}</span>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(createdToken)} onOpenChange={open => !open && setCreatedToken(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle><span key={tUi("copyYourAPIKeyNow")}>{tUi("copyYourAPIKeyNow")}</span></DialogTitle><DialogDescription><span key={tUi("thisSecretWillNotBeDisplayedAgainStore")}>{tUi("thisSecretWillNotBeDisplayedAgainStore")}</span></DialogDescription></DialogHeader>
          <div className="rounded-md bg-muted p-3 font-mono text-xs break-all">{createdToken}</div>
          <DialogFooter><Button onClick={copyToken}><Copy className="mr-1.5 h-4 w-4" /> <span><span key={tUi("copyKey")}>{tUi("copyKey")}</span></span></Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deletingId)} onOpenChange={open => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle><span key={tUi("revokeAPIKey")}>{tUi("revokeAPIKey")}</span></AlertDialogTitle><AlertDialogDescription><span key={tUi("anyIntegrationUsingThisKeyWillImmediatelyLose")}>{tUi("anyIntegrationUsingThisKeyWillImmediatelyLose")}</span></AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction onClick={revokeApiKey} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              <span key={deleteMutation.isPending ? tUi("revokingKey") : tUi("revokeKey")}>{deleteMutation.isPending ? tUi("revokingKey") : tUi("revokeKey")}</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
